/**
 * WeekNavigator — controles de navegación entre semanas del plan.
 *
 * Botones "← Anterior", "Esta semana", "Siguiente →" + indicador
 * "Semana N de M · Fase". Disabled controls cuando estamos en la
 * primera/última semana.
 *
 * Sprint 8 S8.3 (UX-007): bajo el indicador renderizamos el rango de
 * fechas "DD-DD MMM" (o cruce de mes) cuando se provee `weekStartDate`.
 */
import { t } from "@/i18n/es";
import { formatWeekDateRange } from "@/utils/planDates";
import type { TrainingPhase } from "@/types/api";

// v16.3 §G.2: phase `race_week` se incluye por exhaustividad TS aunque
// WeekNavigator NUNCA recibe una race_week en la práctica — PlanPage filtra
// regularWeeks antes de pasar `phase` al navegador y renderiza la
// race_week en RaceWeekSection (sección dedicada fuera de la navegación).
const PHASE_LABELS: Record<TrainingPhase, string> = {
  acumulacion: "phaseAcumulacion",
  intensificacion: "phaseIntensificacion",
  peak: "phasePeak",
  taper: "phaseTaper",
  race_week: "phaseRaceWeek",
};

export interface WeekNavigatorProps {
  currentIndex: number;
  totalWeeks: number;
  currentWeekNumber: number;
  phase: TrainingPhase;
  /** Si != null, la semana actual del plan (today-based). Si null,
   *  el botón "Esta semana" se oculta (plan fuera de window). */
  todayWeekIndex: number | null;
  /** ISO YYYY-MM-DD del primer día de la semana seleccionada. Si se
   *  pasa, se muestra el rango "DD-DD MMM" bajo el indicador (UX-007).
   *  Opcional para mantener compat con tests existentes y consumidores
   *  que no tengan acceso al modelo Week. */
  weekStartDate?: string;
  onChange: (newIndex: number) => void;
}

export function WeekNavigator({
  currentIndex,
  totalWeeks,
  currentWeekNumber,
  phase,
  todayWeekIndex,
  weekStartDate,
  onChange,
}: WeekNavigatorProps) {
  const phaseLabel = t.plan[PHASE_LABELS[phase] as keyof typeof t.plan];
  const indicator = t.plan.weekIndicator
    .replace("{n}", String(currentWeekNumber))
    .replace("{total}", String(totalWeeks))
    .replace("{phase}", phaseLabel);

  const dateRange = weekStartDate
    ? formatWeekDateRange(weekStartDate)
    : "";

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < totalWeeks - 1;
  const showThisWeek = todayWeekIndex !== null && todayWeekIndex !== currentIndex;
  // v14_2 §4: la semana mostrada ES la que contiene hoy → badge "Esta
  // semana" para distinguirla. Mutuamente excluyente con `showThisWeek`
  // (el botón-salto): si estás EN la semana actual se muestra el badge;
  // si NO, el botón para saltar a ella.
  const isCurrentWeek = todayWeekIndex !== null && todayWeekIndex === currentIndex;

  return (
    <div
      data-testid="week-navigator"
      className="flex items-center justify-between gap-2"
    >
      <button
        type="button"
        data-testid="week-nav-prev"
        onClick={() => onChange(currentIndex - 1)}
        disabled={!canGoPrev}
        aria-label={t.plan.prevWeek}
        className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-bone-muted transition-colors duration-fast hover:border-border-strong hover:text-bone disabled:cursor-not-allowed disabled:opacity-30"
      >
        ← {t.plan.prevWeek}
      </button>

      <div className="flex flex-col items-center text-center">
        <p
          className="text-sm text-bone"
          data-testid="week-indicator"
        >
          {indicator}
        </p>
        {dateRange && (
          <p
            data-testid="week-date-range"
            className="text-xs text-bone-muted"
          >
            {dateRange}
          </p>
        )}
        {isCurrentWeek && (
          <span
            data-testid="week-current-badge"
            className="mt-1 rounded-full border border-lime/50 bg-lime/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-lime"
          >
            {t.plan.thisWeek}
          </span>
        )}
        {showThisWeek && (
          <button
            type="button"
            data-testid="week-nav-today"
            onClick={() => onChange(todayWeekIndex)}
            className="mt-1 text-xs text-lime underline-offset-4 hover:underline"
          >
            {/* s63 (S-3 #5): label propio — compartía texto exacto con
                el badge "ESTA SEMANA" y en semanas no-actuales se leía
                como si la semana mostrada fuera la actual. */}
            {t.plan.goToThisWeek}
          </button>
        )}
      </div>

      <button
        type="button"
        data-testid="week-nav-next"
        onClick={() => onChange(currentIndex + 1)}
        disabled={!canGoNext}
        aria-label={t.plan.nextWeek}
        className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-bone-muted transition-colors duration-fast hover:border-border-strong hover:text-bone disabled:cursor-not-allowed disabled:opacity-30"
      >
        {t.plan.nextWeek} →
      </button>
    </div>
  );
}
